import * as React from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFieldArray, useForm, type UseFormReturn } from "react-hook-form";
import { Building2, ChevronDown, History, Save, X } from "lucide-react";
import { apiFetch, APIError, NetworkError } from "@/lib/api-client";
import { enqueueMutation } from "@/lib/outbox";
import { toast } from "@/components/ui/toaster";
import { HeatBadge } from "@/components/ui/heat-badge";
import { MeasuresMultiselect } from "@/components/ui/measures-multiselect";
import { Field } from "@/components/ui/field";
import { PageHeader } from "@/components/layout/page-header";
import { STUFEN_SELECT_OPTIONS, type ConfirmedLevel } from "@/lib/stufen-labels";
import { cn } from "@/lib/utils";
import type { Room, RoomListResponse } from "../rooms/types";

/**
 * SammelModusRoute — Multi-Raum-Eintrag in einer Maske (Phase 13 C.4).
 *
 * Pattern: useFieldArray für Rows pro Raum, eine Row je Raum. Quick-fill
 * lädt gestrige/letzte Werte und prefilled die Masken. Submit als ein
 * POST /v1/entries/bulk mit allen ausgefüllten Rows.
 *
 * Idempotenz via bulk_client_uuid (handler returns 200/skipped bei Replay).
 * NetworkError → outbox-enqueue, success-toast mit "offline"-Hinweis.
 *
 * Temp-Range -10..60°C konsistent mit entry-create-route.
 *
 * B3: Dropdown-Optionen 0-3 (DB-Wert-aligned) via STUFEN_SELECT_OPTIONS.
 * B20: classifyIndoor gibt 0-3 zurück (war 1-4), Vorschau-Badge nutzt DB-Wert direkt.
 * B6: Card-pro-Raum-Stack, alle Felder auf jedem Breakpoint erreichbar;
 *     < lg aufklappbar (Auto-Expand bei erster Temp), lg+ Detail immer offen
 *     + 2/3-Spalten-Grid. Tabellen-Render-Pfad entfernt.
 */

type LevelOverride = "" | "0" | "1" | "2" | "3";

interface Row {
  room_id: string;
  room_name: string;
  indoor_temp_c: string; // leer = nicht ausgefüllt
  level_override: LevelOverride;
  measures_taken: string[];
  notes: string;
}

interface FormData {
  rows: Row[];
}

// ─── RoomEntryCard ────────────────────────────────────────────────────────────

interface RoomEntryCardProps {
  field: { id: string; room_id: string; room_name: string };
  idx: number;
  form: UseFormReturn<FormData>;
  row: Row | undefined;
  onClear: (idx: number) => void;
}

function RoomEntryCard({ field, idx, form, row, onClear }: RoomEntryCardProps): React.ReactElement {
  const headingId = `room-card-${field.room_id}`;
  const detailId = `room-detail-${field.room_id}`;

  const tempVal = row ? Number(row.indoor_temp_c) : NaN;
  const preview = Number.isFinite(tempVal) ? classifyIndoor(tempVal) : null;
  const filled = row ? isFilled(row) : false;

  // Detail-Sektion (Stufen-Override/Notiz/Maßnahmen) klappt NUR auf User-Wunsch
  // auf (Chevron) — kein Auto-Expand, damit die Masse-Eingabe kompakt bleibt
  // und alle Räume als Zeilen sichtbar/schnell befüllbar sind. Das Temp-Input
  // liegt im immer-sichtbaren Header, also ist Eintragen ohne Aufklappen möglich.
  const [expanded, setExpanded] = React.useState(false);

  return (
    <li>
      <section
        role="group"
        aria-labelledby={headingId}
        className={cn(
          "rounded-lg border-l-4 bg-[color:var(--color-surface)] shadow-[var(--shadow-sm)]",
          "transition-colors duration-150 ease-out",
          filled
            ? "border border-[color:var(--color-brand-primary)] border-l-[color:var(--color-brand-primary)]"
            : "border border-[color:var(--color-border)] border-l-transparent",
        )}
      >
        {/* ── Card-Header (immer sichtbar) ──────────────────────────── */}
        <div
          className={cn(
            "flex flex-wrap items-center gap-3 p-3 sm:p-4",
            filled && "bg-[color:var(--color-brand-primary-soft)]",
            "transition-colors duration-150 ease-out",
          )}
        >
          {/* Zone A — Raum-Name */}
          <h3
            id={headingId}
            className="min-w-0 flex-1 truncate text-base font-semibold leading-tight text-[color:var(--color-text-primary)]"
          >
            {field.room_name}
          </h3>

          {/* Zone B — Temp-Input */}
          <div className="flex shrink-0 items-center gap-1.5">
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min={-10}
              max={60}
              aria-label={`Innentemperatur ${field.room_name} in Grad Celsius`}
              placeholder="—"
              className={cn(
                "h-14 w-20 rounded-md border bg-white px-2 text-center",
                "text-lg font-semibold tabular-nums",
                "border-[color:var(--color-border-strong)]",
                "focus:border-[color:var(--color-border-focus)] focus:outline-none",
                "focus-visible:ring-2 focus-visible:ring-[color:var(--color-border-focus)] focus-visible:ring-offset-1",
              )}
              {...form.register(`rows.${idx}.indoor_temp_c`)}
              onKeyDown={(e) => {
                // Enter/Go springt zum nächsten Raum-Temp-Feld statt das
                // Formular abzusenden (schnelle Reihen-Eingabe; verhindert
                // versehentliches Submit). Letztes Feld: no-op, kein Submit.
                if (e.key === "Enter") {
                  e.preventDefault();
                  const next = document.querySelector<HTMLInputElement>(
                    `input[name="rows.${idx + 1}.indoor_temp_c"]`,
                  );
                  next?.focus();
                  next?.select();
                }
              }}
            />
            <span aria-hidden="true" className="text-sm text-[color:var(--color-text-secondary)]">
              °C
            </span>
          </div>

          {/* Zone C — Badge + Toggle-Chevron */}
          <div className="flex shrink-0 items-center gap-2">
            {/* HeatBadge immer sichtbar (das war der Kern-Bug) */}
            {preview ? (
              <span className="inline-flex items-center gap-1">
                <HeatBadge level={preview.level} compact />
                {preview.tentative && (
                  <span
                    title="Stufe 1 nur bei warmer Außentemperatur — Server entscheidet final"
                    aria-label="vorläufig"
                    className="text-[0.7rem] text-[color:var(--color-text-muted)]"
                  >
                    (?)
                  </span>
                )}
              </span>
            ) : (
              <span className="text-xs text-[color:var(--color-text-muted)]" aria-hidden="true">
                —
              </span>
            )}

            {/* Chevron-Toggle — nur < lg; ab lg ist Detail immer offen */}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={detailId}
              aria-label={
                expanded
                  ? `Details für ${field.room_name} einklappen`
                  : `Details für ${field.room_name} aufklappen`
              }
              className={cn(
                "inline-flex size-14 shrink-0 items-center justify-center rounded-md lg:hidden",
                "text-[color:var(--color-text-secondary)]",
                "hover:bg-[color:var(--color-surface-sunken)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-border-focus)] focus-visible:ring-offset-1",
                "transition-transform duration-150 ease-out motion-reduce:transition-none",
              )}
            >
              <ChevronDown
                aria-hidden="true"
                strokeWidth={2.25}
                className={cn("size-5 transition-transform duration-150 ease-out motion-reduce:transition-none", expanded && "rotate-180")}
              />
            </button>
          </div>
        </div>

        {/* ── Detail-Bereich ────────────────────────────────────────── */}
        {/* < lg: state-gesteuert via hidden/block; lg+: lg:block überschreibt */}
        <div
          id={detailId}
          role="region"
          aria-labelledby={headingId}
          className={cn(
            "border-t border-[color:var(--color-border)] px-3 pb-4 pt-3 sm:px-4",
            "space-y-4",
            expanded ? "block anim-stagger-in" : "hidden",
            "lg:block",
          )}
        >
          {/* Stufen-Override */}
          <Field label="Stufen-Override">
            <select
              aria-label={`Stufen-Override ${field.room_name}`}
              className={cn(
                "h-14 w-full rounded-md border bg-white px-3 text-base",
                "border-[color:var(--color-border-strong)]",
                "focus:border-[color:var(--color-border-focus)] focus:outline-none",
                "focus-visible:ring-2 focus-visible:ring-[color:var(--color-border-focus)] focus-visible:ring-offset-1",
              )}
              {...form.register(`rows.${idx}.level_override`)}
            >
              <option value="">Automatisch (aus Temperatur)</option>
              {STUFEN_SELECT_OPTIONS.map((opt) => (
                <option key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          {/* Notiz */}
          <Field label="Notiz">
            <input
              type="text"
              aria-label={`Notiz ${field.room_name}`}
              placeholder="z.B. Fenster verschattet, Lüften 14 Uhr"
              className={cn(
                "h-14 w-full rounded-md border bg-white px-3 text-base",
                "border-[color:var(--color-border-strong)]",
                "focus:border-[color:var(--color-border-focus)] focus:outline-none",
                "focus-visible:ring-2 focus-visible:ring-[color:var(--color-border-focus)] focus-visible:ring-offset-1",
              )}
              {...form.register(`rows.${idx}.notes`)}
            />
          </Field>

          {/* Maßnahmen — volle Card-Breite, kein w-64-Hack */}
          <div>
            <p className="mb-1.5 text-sm font-medium text-[color:var(--color-text-secondary)]">
              Maßnahmen
            </p>
            <MeasuresMultiselect
              level={preview?.level}
              selectedCodes={row?.measures_taken ?? []}
              onChange={(codes) => form.setValue(`rows.${idx}.measures_taken`, codes)}
            />
          </div>

          {/* Clear-Action — nur wenn Card gefüllt */}
          {filled && (
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={() => onClear(idx)}
                aria-label={`Eingaben für ${field.room_name} verwerfen`}
                className={cn(
                  "inline-flex h-14 items-center gap-2 rounded-md px-3",
                  "text-sm font-medium text-[color:var(--color-text-secondary)]",
                  "hover:bg-[color:var(--color-surface-sunken)] hover:text-[color:var(--color-danger)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-border-focus)] focus-visible:ring-offset-1",
                  "transition-colors duration-150 ease-out",
                )}
              >
                <X aria-hidden="true" strokeWidth={2} className="size-4" />
                Eingaben verwerfen
              </button>
            </div>
          )}
        </div>
      </section>
    </li>
  );
}

interface EntryHistoryItem {
  room_id: string;
  indoor_temp_c: number;
  measures_notes?: string;
}

/**
 * classifyIndoor — B20-konforme Vorschau-Klassifikation (0-3, DB-Wert-aligned).
 *
 * Stufe 1 nur bei indoor>26. Da outdoor unbekannt ist, ist Stufe 1 "tentative"
 * (wird in der Vorschau-Zelle mit einem "(?)"-Qualifier gerendert).
 */
function classifyIndoor(temp: number): {
  level: ConfirmedLevel;
  tentative: boolean;
} {
  if (temp > 35) return { level: 3, tentative: false };
  if (temp > 30) return { level: 2, tentative: false };
  if (temp > 26) return { level: 1, tentative: true }; // outdoor unbekannt
  return { level: 0, tentative: false };
}

function isFilled(row: Row): boolean {
  if (row.indoor_temp_c === "") return false;
  const n = Number(row.indoor_temp_c);
  return Number.isFinite(n);
}

export function SammelModusRoute(): React.ReactElement {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const rooms = useQuery({
    queryKey: ["rooms", "list"],
    queryFn: () => apiFetch<RoomListResponse>("/v1/rooms"),
  });

  const form = useForm<FormData>({
    defaultValues: { rows: [] },
  });

  const { fields, replace } = useFieldArray({ control: form.control, name: "rows" });

  React.useEffect(() => {
    if (rooms.data?.rooms && fields.length === 0) {
      replace(
        rooms.data.rooms.map((r: Room) => ({
          room_id: r.id,
          room_name: r.name,
          indoor_temp_c: "",
          level_override: "" as LevelOverride,
          measures_taken: [],
          notes: "",
        })),
      );
    }
  }, [rooms.data, fields.length, replace]);

  const watchedRows = form.watch("rows");
  const filledCount = watchedRows.filter(isFilled).length;

  const applyHistory = (entries: EntryHistoryItem[]) => {
    const byRoom = new Map(entries.map((e) => [e.room_id, e]));
    watchedRows.forEach((row, idx) => {
      const prev = byRoom.get(row.room_id);
      if (prev) {
        form.setValue(`rows.${idx}.indoor_temp_c`, String(prev.indoor_temp_c));
        if (prev.measures_notes) form.setValue(`rows.${idx}.notes`, prev.measures_notes);
      }
    });
  };

  const loadLastEntry = async () => {
    try {
      const res = await apiFetch<{ entries: EntryHistoryItem[] }>(
        "/v1/entries?aggregate=last_per_room",
      );
      applyHistory(res.entries);
      toast("Letzte Werte übernommen", "info");
    } catch {
      toast("Letzte Werte konnten nicht geladen werden", "warning");
    }
  };

  const clearRow = (idx: number) => {
    form.setValue(`rows.${idx}.indoor_temp_c`, "");
    form.setValue(`rows.${idx}.level_override`, "" as LevelOverride);
    form.setValue(`rows.${idx}.measures_taken`, []);
    form.setValue(`rows.${idx}.notes`, "");
  };

  const submit = useMutation({
    mutationFn: async (data: FormData) => {
      const bulkClientUuid = crypto.randomUUID();
      const filledRows = data.rows.filter(isFilled).map((row) => {
        const body: Record<string, unknown> = {
          room_id: row.room_id,
          client_uuid: crypto.randomUUID(),
          indoor_temp_c: Number(row.indoor_temp_c),
          event_at: new Date().toISOString(),
        };
        if (row.level_override !== "") {
          body.confirmed_level = Number(row.level_override);
        }
        if (row.measures_taken.length > 0) {
          body.measures_taken = row.measures_taken;
        }
        if (row.notes) body.measures_notes = row.notes;
        return body;
      });
      const payload = { bulk_client_uuid: bulkClientUuid, entries: filledRows };
      try {
        return await apiFetch("/v1/entries/bulk", { method: "POST", body: payload });
      } catch (err) {
        if (err instanceof NetworkError) {
          await enqueueMutation({
            method: "POST",
            url: "/v1/entries/bulk",
            body: payload,
            clientUuid: bulkClientUuid,
          });
          return { offline: true, count: filledRows.length };
        }
        throw err;
      }
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      void qc.invalidateQueries({ queryKey: ["entries"] });
      void qc.invalidateQueries({ queryKey: ["rooms"] });
      const offline = Boolean(
        result && typeof result === "object" && "offline" in result &&
          (result as { offline?: boolean }).offline,
      );
      if (offline) {
        const count = (result as { count: number }).count;
        toast(`${count} Einträge lokal gespeichert — Sync startet sobald online`, "warning", 7000);
      } else {
        toast(`${filledCount} Einträge dokumentiert`, "success");
      }
      navigate("/dashboard");
    },
    onError: (err) => {
      if (err instanceof APIError) {
        toast(`Fehler: ${err.code}`, "danger");
      } else {
        toast("Speichern fehlgeschlagen", "danger");
      }
    },
  });

  if (rooms.isPending) {
    return (
      <div className="space-y-6">
        <div className="h-10 animate-pulse rounded bg-[color:var(--color-surface-sunken)]" />
        <div className="h-12 animate-pulse rounded-lg bg-[color:var(--color-surface-sunken)]" />
        <div className="h-64 animate-pulse rounded-lg bg-[color:var(--color-surface-sunken)]" />
      </div>
    );
  }

  if (!rooms.data?.rooms.length) {
    return (
      <div className="space-y-6">
        <PageHeader title="Sammel-Modus" />
        <section className="rounded-lg border border-[color:var(--color-border)] bg-white p-6">
          <Building2
            aria-hidden="true"
            strokeWidth={2}
            className="size-8 text-[color:var(--color-text-secondary)]"
          />
          <h2 className="mt-3 text-xl font-semibold">Noch keine Räume angelegt</h2>
          <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
            Bevor Sie Messungen sammeln können, muss mindestens ein Raum existieren.
          </p>
          <Link
            to="/raeume"
            className="mt-4 inline-flex h-11 items-center rounded-md bg-[color:var(--color-brand-primary-strong)] px-5 text-sm font-semibold text-white hover:bg-[color:var(--color-warm-orange-800)]"
          >
            Räume verwalten
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sammel-Modus"
        actions={
          <button
            type="submit"
            form="sammel-form"
            disabled={submit.isPending || filledCount === 0}
            className={cn(
              "inline-flex h-14 items-center gap-2 rounded-md px-5",
              "bg-[color:var(--color-brand-primary-strong)]",
              "text-sm font-semibold text-[color:var(--color-text-on-primary)]",
              "transition-colors duration-150 ease-out",
              "hover:bg-[color:var(--color-warm-orange-800)]",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <Save aria-hidden="true" strokeWidth={2.25} className="size-4" />
            {submit.isPending
              ? "Speichere …"
              : `Speichern (${filledCount} ${filledCount === 1 ? "Raum" : "Räume"})`}
          </button>
        }
      />

      <section
        aria-labelledby="quickfill-heading"
        className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-4"
      >
        <h2
          id="quickfill-heading"
          className="text-xs font-medium uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-text-secondary)]"
        >
          Schnell befüllen
        </h2>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={loadLastEntry}
            className="inline-flex h-14 items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-white px-4 text-sm font-medium hover:bg-[color:var(--color-surface-sunken)]"
          >
            <History aria-hidden="true" strokeWidth={2} className="size-4" />
            Letzte Werte pro Raum
          </button>
        </div>
      </section>

      <form
        id="sammel-form"
        onSubmit={form.handleSubmit((d) => submit.mutate(d))}
        noValidate
      >
        <ul
          role="list"
          className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4 2xl:grid-cols-3"
        >
          {fields.map((field, idx) => (
            <RoomEntryCard
              key={field.id}
              field={field}
              idx={idx}
              form={form}
              row={watchedRows[idx]}
              onClear={clearRow}
            />
          ))}
        </ul>
      </form>

      <p className="text-xs text-[color:var(--color-text-secondary)]">
        Die Stufen-Vorschau ist nur Orientierung — die finale Klassifikation berechnet der Server.
        Räume ohne Temperatur werden nicht gespeichert.
      </p>
    </div>
  );
}

SammelModusRoute.displayName = "SammelModusRoute";
